import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import axios from "axios";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import { TinyBusy } from "../../components/tiny/TinyLoading";

const API_BASE = "/api/tiny";

// 計算下一週（週一到週日）
const getNextWeek = (weekStr) => {
  const startStr = weekStr.split("-")[0];
  const year = parseInt(startStr.substring(0, 4), 10);
  const month = parseInt(startStr.substring(4, 6), 10) - 1;
  const day = parseInt(startStr.substring(6, 8), 10);

  const startDate = new Date(year, month, day);
  const nextMonday = new Date(startDate);
  nextMonday.setDate(startDate.getDate() + 7);
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}${m}${d}`;
  };

  return `${formatDate(nextMonday)}-${formatDate(nextSunday)}`;
};

function TinySettlementImportTab() {
  const [weeks, setWeeks] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // v2.40.0：首屏的週期 / 活動類型清單原本沒有任何指示，下拉是空的也不知道是還在載
  const [baseLoading, setBaseLoading] = useState(true);

  const [week, setWeek] = useState("");
  const [activityType, setActivityType] = useState("");
  const [activityName, setActivityName] = useState("");
  const [isCustomType, setIsCustomType] = useState(false);
  const [inputText, setInputText] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preview, setPreview] = useState(null);
  const [statusFilter, setStatusFilter] = useState({
    valid: true,
    warning: false,
    rejected: false,
  });

  const loadBaseData = useCallback(async () => {
    setBaseLoading(true);
    try {
      const [weeksRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE}/data/weeks`),
        axios.get(`${API_BASE}/settlements/activity-types`),
      ]);

      const dbWeeks = weeksRes.data?.data?.weeks || [];
      if (dbWeeks.length > 0) {
        const nextWeek = getNextWeek(dbWeeks[0]);
        const weeksWithNext = [nextWeek, ...dbWeeks];
        setWeeks(weeksWithNext);
        if (!week) {
          setWeek(nextWeek);
        }
      } else {
        setWeeks([]);
      }

      const typeValues = typesRes.data?.data?.types || [];
      setTypes(typeValues);
      if (!activityType && typeValues.length > 0) {
        const aaGuardian = typeValues.find((t) => t.id === "aa_guardian");
        const selectedType = aaGuardian || typeValues[0];
        setActivityType(selectedType.id);
        setActivityName(selectedType.name);
      }
    } catch (err) {
      setError(`載入基礎資料失敗: ${err.response?.data?.msg || err.message}`);
    } finally {
      setBaseLoading(false);
    }
  }, [week, activityType]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const handlePreview = async () => {
    setError("");
    setSuccess("");
    setPreview(null);

    if (!week || (!activityType && !isCustomType)) {
      setError("請先選擇週期與活動類型");
      return;
    }
    if (isCustomType && !activityName.trim()) {
      setError("自定義類型請輸入活動名稱");
      return;
    }

    const csvText = inputText || "";
    if (!csvText.trim()) {
      setError("請貼上 CSV 內容");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/settlement-center/import/preview`, {
        week,
        activity_type: isCustomType ? "__custom_preview__" : activityType,
        activity_name: activityName,
        format: "csv",
        csv_text: csvText,
      });
      if (res.data.code === 1) {
        setPreview(res.data.data);
      } else {
        setError(res.data.msg || "預覽失敗");
      }
    } catch (err) {
      setError(`預覽失敗: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!preview?.rows?.length) {
      setError("沒有可匯入資料");
      return;
    }
    try {
      setSaving(true);
      let effectiveType = activityType;

      if (isCustomType) {
        const typeId = `custom_${Date.now()}`;
        const createRes = await axios.post(`${API_BASE}/settlements/activity-types`, {
          id: typeId,
          name: activityName.trim(),
          description: activityName.trim(),
        });
        if (createRes.data.code !== 1) {
          setError(createRes.data.msg || "創建自定義活動類型失敗");
          return;
        }
        effectiveType = typeId;
      }

      const res = await axios.post(`${API_BASE}/settlement-center/import/confirm`, {
        week,
        activity_type: effectiveType,
        activity_name: activityName,
        rows: preview.rows,
        imported_by: "web",
      });
      if (res.data.code === 1) {
        setSuccess(`匯入成功，批次 ${res.data.data.batch_id}`);
        setPreview(null);
        if (isCustomType) {
          setIsCustomType(false);
          setActivityType(effectiveType);
          loadBaseData();
        }
      } else {
        setError(res.data.msg || "匯入失敗");
      }
    } catch (err) {
      setError(`匯入失敗: ${err.response?.data?.msg || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const summary = preview?.summary || {};
  const statusLabelMap = {
    valid: "有效",
    warning: "警告",
    rejected: "拒絕",
  };
  const statusColorMap = {
    valid: {
      bg: "linear-gradient(135deg, #2dce89 0%, #26a770 100%)",
      text: "#fff",
      border: "rgba(45,206,137,0.45)",
      softBg: "rgba(45,206,137,0.15)",
      softText: "#2dce89",
    },
    warning: {
      bg: "linear-gradient(135deg, #fb6340 0%, #fbb140 100%)",
      text: "#fff",
      border: "rgba(251,99,64,0.45)",
      softBg: "rgba(251,99,64,0.15)",
      softText: "#fb6340",
    },
    rejected: {
      bg: "linear-gradient(135deg, #f5365c 0%, #f56036 100%)",
      text: "#fff",
      border: "rgba(245,54,92,0.45)",
      softBg: "rgba(245,54,92,0.15)",
      softText: "#f5365c",
    },
  };
  const filteredRows = (preview?.rows || []).filter((r) => !!statusFilter[r.validation_status]);
  const inputStyle = {
    background: "#1a1f2b",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "6px",
  };

  return (
    <Card style={{ background: "#1e2530", border: "1px solid rgba(255,255,255,0.1)" }}>
      <CardBody style={{ padding: "20px" }}>
        {error && (
          <Alert color="danger" toggle={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && <Alert color="success">{success}</Alert>}

        {baseLoading && (
          <div className="mb-2">
            <TinyBusy text="載入週期與活動類型清單" />
          </div>
        )}
        <Row>
          <Col md={3}>
            <TinyWeekSelector
              label="週期"
              options={weeks}
              value={week}
              onChange={setWeek}
              includeEmptyOption
              markFirstAsNextWeek
              navInline={false}
              inputStyle={inputStyle}
            />
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>活動類型</Label>
              <Input
                type="select"
                value={isCustomType ? "__custom__" : activityType}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "__custom__") {
                    setIsCustomType(true);
                    setActivityType("");
                    setActivityName("");
                  } else {
                    setIsCustomType(false);
                    const selected = types.find((t) => t.id === val);
                    setActivityType(val);
                    if (selected) setActivityName(selected.name);
                  }
                }}
                style={inputStyle}
              >
                <option value="">選擇活動類型</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
                <option value="__custom__">── 自定義 ──</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>活動名稱</Label>
              <Input value={activityName} onChange={(e) => setActivityName(e.target.value)} style={inputStyle} />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>格式</Label>
              <Input value="CSV" disabled style={inputStyle} />
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
            貼上 CSV 內容
          </Label>
          <Input
            type="textarea"
            rows={10}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12 }}
          />
          <small style={{ color: "rgba(255,255,255,0.65)" }}>
            系統會自動過濾：僅匯入「交收情況=未交收」，且填寫時間落在目標週期正負一天內的資料。
          </small>
        </FormGroup>

        <Row className="mb-3">
          <Col>
            <Button color="primary" onClick={handlePreview} disabled={loading}>
              {loading ? <TinyBusy light /> : "預覽檢核"}
            </Button>
          </Col>
        </Row>

        {preview && (
          <>
            <Row className="mb-2">
              <Col>
                <span
                  style={{
                    display: "inline-block",
                    background: statusColorMap.valid.bg,
                    color: statusColorMap.valid.text,
                    padding: "4px 10px",
                    borderRadius: "999px",
                    fontSize: "11px",
                    fontWeight: 700,
                    marginRight: "8px",
                  }}
                >
                  有效 {summary.valid_rows || 0}
                </span>
                <span
                  style={{
                    display: "inline-block",
                    background: statusColorMap.warning.bg,
                    color: statusColorMap.warning.text,
                    padding: "4px 10px",
                    borderRadius: "999px",
                    fontSize: "11px",
                    fontWeight: 700,
                    marginRight: "8px",
                  }}
                >
                  警告 {summary.warning_rows || 0}
                </span>
                <span
                  style={{
                    display: "inline-block",
                    background: statusColorMap.rejected.bg,
                    color: statusColorMap.rejected.text,
                    padding: "4px 10px",
                    borderRadius: "999px",
                    fontSize: "11px",
                    fontWeight: 700,
                    marginRight: "8px",
                  }}
                >
                  拒絕 {summary.rejected_rows || 0}
                </span>
                <span
                  style={{
                    display: "inline-block",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    padding: "4px 10px",
                    borderRadius: "999px",
                    fontSize: "11px",
                    fontWeight: 700,
                  }}
                >
                  匯入總金額 {summary.accepted_amount || 0}
                </span>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={8}>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", marginTop: "4px" }}>
                  預覽狀態篩選
                  <div style={{ marginTop: "6px" }}>
                    {["valid", "warning", "rejected"].map((k) => {
                      const active = !!statusFilter[k];
                      const c = statusColorMap[k];
                      return (
                        <button
                          key={k}
                          onClick={() => setStatusFilter((prev) => ({ ...prev, [k]: !prev[k] }))}
                          style={{
                            background: active ? c.bg : c.softBg,
                            color: active ? c.text : c.softText,
                            border: active ? "none" : `1px solid ${c.border}`,
                            padding: "5px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: "pointer",
                            marginRight: "6px",
                            boxShadow: active ? "0 3px 8px rgba(0,0,0,0.25)" : "none",
                          }}
                        >
                          {statusLabelMap[k]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-right">
                <Button color="success" onClick={handleConfirmImport} disabled={saving}>
                  {saving ? <TinyBusy light /> : "確認匯入"}
                </Button>
              </Col>
            </Row>
            <div style={{ maxHeight: 380, overflow: "auto" }}>
              <Table responsive hover size="sm" style={{ color: "rgba(255,255,255,0.85)" }}>
                <thead style={{ background: "#252d3a", color: "#fff" }}>
                  <tr>
                    <th>#</th>
                    <th>狀態</th>
                    <th>填寫時間</th>
                    <th>交收情況</th>
                    <th>俱樂部</th>
                    <th>玩家</th>
                    <th>超代</th>
                    <th>代理</th>
                    <th>金額</th>
                    <th>訊息</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.row_no}>
                      <td>{r.row_no}</td>
                      <td>
                        <span
                          style={{
                            display: "inline-block",
                            background: statusColorMap[r.validation_status]?.bg || statusColorMap.rejected.bg,
                            color: "#fff",
                            padding: "3px 8px",
                            borderRadius: "999px",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.2px",
                          }}
                        >
                          {statusLabelMap[r.validation_status] || r.validation_status}
                        </span>
                      </td>
                      <td>{r.fill_time_raw || "-"}</td>
                      <td>{r.settlement_status || "-"}</td>
                      <td>{r.club_id || "-"}</td>
                      <td>{r.player_id || "-"}</td>
                      <td>{r.super_agent_id || "-"}</td>
                      <td>{r.agent_id || "-"}</td>
                      <td>{r.amount}</td>
                      <td>{(r.validation_messages || []).join("；") || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default TinySettlementImportTab;

